import { useForm, useWatch } from "react-hook-form";
import {
  Button,
  RadioSelector,
  RadioSelectorItem,
} from "@sellar-io/components";
import { Icon, SpinnerLegacy } from "../../../../../components";
import { Alert } from "~/components/alert/Alert";
import {
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "~/components/modal/Dialog";
import { useFullPageDialogContext } from "~/components/modal/useFullPageDialogContext";
import { apiV2AssertError } from "~/hooks/api/v2/apiV2Request";
import { useProductStatusUpdate } from "~/hooks/api/v2/product/useProductStatusUpdate";
import { ActiveProductCategoryDialogPages } from "../ActiveProductCategoryDialogTypes";

export const StatusPage = ({
  productId,
  productActive,
  onConfirm,
  onDismiss,
}: {
  productId: number;
  productActive: boolean;
  onConfirm: (selectedActive: boolean) => void;
  onDismiss: () => void;
}) => {
  const { setPage } = useFullPageDialogContext();
  const productUpdateMutation = useProductStatusUpdate(productId);
  const { handleSubmit, control, setValue } = useForm<{
    active: "active" | "inactive";
  }>({
    defaultValues: {
      active: productActive ? "active" : "inactive",
    },
    shouldUseNativeValidation: true,
  });

  const selectedValue = useWatch({
    control,
    name: "active",
  });
  const isGoingActive = !productActive && selectedValue === "active";

  const handleFormSubmit = handleSubmit((data) => {
    productUpdateMutation.mutate(
      {
        active: data.active === "active",
      },
      {
        onSuccess: () => {
          if (isGoingActive) {
            setPage(ActiveProductCategoryDialogPages.PRODUCT_ITEMS);
          } else {
            onConfirm(data.active === "active");
          }
        },
      },
    );
  });

  return (
    <>
      <DialogNavigation
        leftIcon="CHEVRON_DOWN"
        onClickLeftIcon={onDismiss}
        title="Update product status"
        description="Manage how your product gets shown on your storefront and in the new release area for you customers."
      />
      <form onSubmit={handleFormSubmit} className="flex flex-col gap-2">
        <DialogContent>
          {productUpdateMutation.isPending ? (
            <SpinnerLegacy />
          ) : (
            <>
              {productUpdateMutation.isError && (
                <Alert
                  type="error"
                  message={
                    apiV2AssertError(productUpdateMutation.error)?.response
                      ?.data.message ?? "An unknown error occurred"
                  }
                />
              )}
              <RadioSelector
                onChange={(e) =>
                  setValue(
                    "active",
                    e.currentTarget.value as "active" | "inactive",
                  )
                }
                value={selectedValue}
                required={true}
                variant="grouped"
              >
                <RadioSelectorItem
                  value="inactive"
                  title="Inactive"
                  description="Product will be hidden from all customers. You'll still be able to manage stock levels, pricing and product details."
                />
                <RadioSelectorItem
                  value="active"
                  title="Active"
                  description="Product will be shown to customers. Your price tier visibility settings for individual product items will still apply."
                />
              </RadioSelector>
            </>
          )}
        </DialogContent>
        <DialogCTA>
          <Button type="submit">
            {isGoingActive ? (
              <>
                Next <Icon icon="CHEVRON_RIGHT" />
              </>
            ) : (
              "Confirm"
            )}
          </Button>
        </DialogCTA>
      </form>
    </>
  );
};
